<template>
  <GridChartCard
  :title="title"
  :subtitle="subtitle"
  ref="chartCard"
  :hasData="data.data.length > 0"
  :disableCsv="false"
  @draw="createChart"
  :tooltip="tooltip"
  :noDataText="noDataText">
    <template #actions>
      <Icon
      v-if="isInteractive && icon && showSecondaryGrouping"
      iconColor="white"
      :tooltipColor="contractMode.details.color"
      :tooltipText="secondaryGroupingTooltip"
      :small="false"
      margin="mr-3"
      :disabled="data.data.length === 0 || data.dataSets.sets.length <= 1"
      @icon-clicked="secondaryGrouping = !secondaryGrouping"
      :icon="icon"
      elementId="toggle-secondary-grouping"/>
    </template>
  </GridChartCard>
</template>

<script>
import { Chart } from 'chart.js'
import { mapActions, mapGetters } from 'vuex'
import { getChartColors, getChartColor } from '@/components/dashboard/ChartColors'
import IconHelpers from '@/utils/IconHelpers'
import { ContractMode } from '@/utils/Enumerations'
import { chartTooltipStyle } from '@/utils/ChartHelpers'
import { numberWithCommas } from '@/utils/NumericMutations'

export default {
  name: 'StackedBarChartCardForVolumeLoads',

  components: {
    GridChartCard: () => import('@/components/dashboard/GridChartCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    IconHelpers,
    chart: null,
    currentData: {},
    secondaryGrouping: true,
    contractMode: ContractMode.Logs
  }),

  props: {
    data: {
      type: Object,
      required: true
    },
    allowSecondaryGrouping: {
      type: Boolean,
      default: true
    },
    isInteractive: {
      type: Boolean,
      required: false,
      default: false
    },
    selectedDestName: {
      type: String,
      required: false,
      default: ''
    },
    tooltip: {
      type: String,
      required: false,
      default: ''
    },
    noDataText: {
      type: String,
      required: false,
      default: 'No data to display'
    },
    loadStatus: {
      type: String,
      required: false,
      default: ''
    },
    type: {
      type: String,
      required: true
    }
  },

  created () {
    this.secondaryGrouping = this.allowSecondaryGrouping
  },

  watch: {
    data: {
      handler (_) {
        this.scheduleDraw()
      },
      deep: true
    },
    secondaryGrouping () {
      this.scheduleDraw()
    }
  },

  computed: {
    ...mapGetters('dashboard', ['interactiveChartTooltipFooter']),
    title () {
      const unit = this.$t('mbf')
      const title = (this.overrideTitle || this.customTitle?.call(null, unit)) ?? `${this.loadStatus ? this.loadStatus + ' ' : ''}${unit} by ${this.type}`

      return (this.selectedDestName && this.$vuetify.breakpoint.lgAndUp)
        ? `${title} (${this.selectedDestName})`
        : title
    },

    subtitle () {
      if (this.$vuetify.breakpoint.mdAndDown && this.selectedDestName) {
        return this.selectedDestName
      }
      return undefined
    },

    secondaryGroupingTooltip () {
      return this.$t(this.secondaryGrouping
        ? 'disableGroupingByCategory'
        : 'groupByCategory',
      {
        category: this.$t(this.data.data[0]?.loadCountAndTract
          ? 'tract' : this.data.miniBarType)
      })
    },

    icon () {
      if (this.data.miniBarType) {
        return IconHelpers.getIconForObjectType(this.data.miniBarType, this.secondaryGrouping)
      } else if (this.data.data[0]?.loadCountAndTract) {
        return IconHelpers.getIconForObjectType('tract', this.secondaryGrouping)
      } else {
        return undefined
      }
    },

    showSecondaryGrouping () {
      return this.data.miniBarType !== 'destination' || this.selectedDestLocationId === -1
    }
  },

  methods: {
    ...mapActions('dashboard', ['getNestedCSV', 'getWidgetInteractionStatus']),

    scheduleDraw () {
      this.$nextTick(() => {
        if (this.$refs.chartCard) this.$refs.chartCard.draw()
      })
    },

    percentOfTotal (value, loads, tractIndex) {
      const mbfArray = loads.map(product => product.mbfData[tractIndex])
      const total = mbfArray.reduce((a, b) => a + b, 0)
      return (value / total * 100).toFixed(1)
    },

    async onDataClicked (e) {
      if (this.isInteractive) {
        const element = this.chart.getElementsAtEventForMode(e, 'point', true)
        const data = this.chart.config._config.data
        const xAxisValue = data.labels[element[0]?.index]
        const yAxisValue = data.datasets[element[0]?.datasetIndex]?.label
        if (await this.getWidgetInteractionStatus({ widgetId: this.widgetId, x: xAxisValue, y: yAxisValue })) {
          this.$emit('data-clicked', {
            xAxisValue: xAxisValue,
            yAxisValue: yAxisValue,
            details: { mismanufactureOnly: false }
          })
        }
      }
    },

    createChart (canvas) {
      const percentOfTotal = this.percentOfTotal
      const dataSets = this.data.dataSets
      const tickFormat = (value) => this.showPercentInTicks ? `${value}%` : value
      const yAxisTitle = {
        display: false,
        text: 'MBF'
      }
      const chart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: dataSets.labels,
          datasets: this.secondaryGrouping && this.showSecondaryGrouping ? dataSets.sets.map((set, index) => {
            return {
              label: set.label,
              data: set.mbfData,
              backgroundColor: getChartColor(index, dataSets.sets.length, ContractMode.Logs.value)
            }
          }) : [{
            data: this.data.data.map(d => d.loadsBySpecies ? d.loadsBySpecies.reduce((acc, curr) => acc + curr.mbf, 0) : d.loadCount ?? d.loadCountAndTract.reduce((acc, curr) => acc + curr.loadCount, 0)),
            backgroundColor: getChartColors(dataSets.sets.length, ContractMode.Logs.value),
            labels: undefined,
            chartType: this.type
          }]
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          resizeDelay: 500,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: this.secondaryGrouping && this.showSecondaryGrouping ? function (toolTipItem) {
                  const value = toolTipItem.parsed.y
                  if (this.secondaryGrouping && this.showSecondaryGrouping === false) {
                    return `${value}`
                  } else {
                    const label = toolTipItem.dataset.label
                    const percent = percentOfTotal(value, dataSets.sets, toolTipItem.dataIndex)
                    return `${label}: ${numberWithCommas(value, 3)} MBF (${percent}%)`
                  }
                } : function (toolTipItem) {
                  return `${numberWithCommas(toolTipItem.parsed.y, 3)} MBF`
                },
                title: function (titleItem) {
                  return titleItem[0].label
                }
              },
              ...chartTooltipStyle
            }
          },
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true,
              ticks: {
                precision: 0,
                callback: function (value) {
                  return tickFormat(value)
                }
              },
              title: {
                display: yAxisTitle.display,
                text: yAxisTitle.text
              }
            }
          },
          onClick: (e) => this.onDataClicked(e)
        }
      })
      if (this.isInteractive) chart.options.plugins.tooltip.callbacks.footer = () => this.interactiveChartTooltipFooter
      this.chart = chart
    },

    refresh () {
      this.$emit('refresh')
    }
  }
}
</script>
